import React, { useContext, useEffect } from "react";
import {
  TextController,
  Text,
  TimeRangeController,
  Divider,
  NumberController,
  CheckboxController,
  DateSelectRangeController,
} from "@pushpress/shared-components";
import { Control, UseFormTrigger, useWatch } from "react-hook-form";
import { EventForm } from "../CalendarForm.utils";
import { AppContext } from "../../../../context/app";
import { getCurrencySymbol } from "../../../../utils/currencyUtils";
import StaffProfileSelect from "../../../../components/controllers/StaffProfileSelect";
import TaxSelect from "../../../../components/controllers/TaxSelect";
import { isValid, parse } from "date-fns";
import {
  DATE_TIME_INPUT_FORMAT,
  TIME_INPUT_FORMAT,
} from "../../../../utils/dateUtils";

type EventDetailsProps = {
  control: Control<EventForm>;
  editMode?: boolean;
  trigger: UseFormTrigger<EventForm>;
};

const EventDetails = (props: EventDetailsProps) => {
  const { control, trigger } = props;

  const app = useContext(AppContext);
  const currencySymbol = getCurrencySymbol(app.client.currencyIso!);

  const [allDay, startDate, endDate, startTime, endTime] = useWatch({
    control,
    name: ["allDay", "date.start", "date.end", "time.start", "time.end"],
  });

  const validateEndTime = (value: string) => {
    // no validate
    if (!startTime) {
      return;
    }

    // no validate if value is invalid
    const endTimeValue = parse(value, TIME_INPUT_FORMAT, new Date());
    if (!isValid(endTimeValue)) {
      return;
    }

    // validate only time when some date is not there
    if (!startDate || !endDate) {
      const startTimeValue = parse(startTime, TIME_INPUT_FORMAT, new Date());
      if (endTimeValue < startTimeValue) {
        return "Less than start";
      }
    }

    // validate date and time
    const startDateTimeValue = parse(
      `${startDate}T${startTime}`,
      DATE_TIME_INPUT_FORMAT,
      new Date(),
    );
    const endDateTimeValue = parse(
      `${endDate}T${value}`,
      DATE_TIME_INPUT_FORMAT,
      new Date(),
    );
    if (endDateTimeValue <= startDateTimeValue) {
      return "Less than start";
    }
  };

  // trigger validation after change values. Avoid fields show outdated messages.
  useEffect(() => {
    if (startDate && endDate) {
      trigger(["date.start", "date.end"]);
    }
    if (startTime && endTime) {
      trigger(["time.start", "time.end"]);
    }
  }, [startDate, endDate, startTime, endTime]);

  return (
    <div className="p-4">
      <TextController
        control={control}
        name="name"
        required
        label="Event name"
      />
      <DateSelectRangeController
        label="Date"
        required
        className="mb-0"
        startDateProps={{
          control,
          required: true,
          autoComplete: "off",
          name: "date.start",
          label: "Date",
        }}
        endDateProps={{
          control,
          required: true,
          autoComplete: "off",
          name: "date.end",
          label: "End date",
        }}
      />
      {!allDay && (
        <TimeRangeController
          label="Time"
          required
          className="mb-0"
          startTimeProps={{
            control,
            name: "time.start",
            required: true,
            label: "Start time",
            valueType: "12-hour",
          }}
          endTimeProps={{
            control,
            name: "time.end",
            required: true,
            label: "End time",
            valueType: "12-hour",
            validate: validateEndTime,
          }}
        />
      )}
      <CheckboxController label="All day" control={control} name="allDay" />
      <Divider vPadding={8} marginRight={-28} />
      <Text variant="heading-5" color="wolf-800" className="mt-3" weight="600">
        Price
      </Text>
      <Text variant="body-md" color="wolf-600" className="mb-4">
        How much does it cost to register
      </Text>
      <NumberController
        label="Price"
        control={control}
        name="price"
        required
        startText={currencySymbol}
        helperText="If free, enter $0"
        min={0}
        className="mb-3"
      />
      <TaxSelect
        control={control}
        name="tax"
        label="Tax"
        placeholder="— Add tax —"
        menuPortalTarget={document.body}
        hideDropdownIndicator
        isClearable
      />
      <Divider vPadding={8} marginRight={-28} />
      <Text variant="heading-5" color="wolf-800" className="mt-3" weight="600">
        Staff
      </Text>
      <Text variant="body-md" color="wolf-600">
        Assign a staff member
      </Text>
      <StaffProfileSelect
        control={control}
        name="mainCoach"
        label="Staff"
        placeholder="— Add staff —"
        menuPortalTarget={document.body}
        hideDropdownIndicator
        hideLabel
        isClearable
      />
    </div>
  );
};

export default EventDetails;
